// ===========================================
// Register Page
// ===========================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Zap, Mail, Lock, User, ArrowRight, Loader, Check } from 'lucide-react';
import './Auth.scss';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Ref for Google Sign-In button container
  const googleButtonRef = useRef(null);
  
  const { register, googleLogin, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();

  // ===========================================
  // Handle Google OAuth Response
  // ===========================================
  // Google Sign-Up works the same as Sign-In (creates account if new)
  const handleGoogleResponse = useCallback(async (response) => {
    setFormError('');
    setIsSubmitting(true);
    
    // Google login will create a new account if user doesn't exist
    const result = await googleLogin(response.credential, true);
    
    if (!result.success) {
      setFormError(result.error);
    }
    
    setIsSubmitting(false);
  }, [googleLogin]);

  // ===========================================
  // Initialize Google Sign-In Button
  // ===========================================
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse
        });
        
        // Render the Google Sign-Up button
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          { 
            theme: 'filled_black',    // Dark theme to match DevPad aesthetic
            size: 'large',
            width: 320,               // Fixed width for consistency
            text: 'signup_with',      // "Sign up with Google"
            shape: 'rectangular'
          }
        );
      }
    };

    // Check if script is already loaded, otherwise wait for it
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleSignIn();
        }
      }, 100);
      
      return () => clearInterval(checkGoogle);
    }
  }, [handleGoogleResponse]);

  // Redirect if already authenticated - go to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Password requirements check
  const passwordChecks = {
    length: password.length >= 8,
    number: /\d/.test(password),
    match: password === confirmPassword && confirmPassword !== ''
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    // Validation
    if (!name || !email || !password || !confirmPassword) {
      setFormError('Please fill in all fields');
      return;
    }
    
    if (!passwordChecks.length || !passwordChecks.number) {
      setFormError('Password does not meet requirements');
      return;
    }
    
    if (!passwordChecks.match) {
      setFormError('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    const result = await register(email, password, name);
    
    if (!result.success) {
      setFormError(result.error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="auth-page">
      {/* Background decoration */}
      <div className="auth-page__bg">
        <div className="auth-page__gradient"></div>
        <div className="auth-page__grid"></div>
      </div>

      <div className="auth-container">
        {/* Logo */}
        <div className="auth-logo">
          <div className="auth-logo__icon">
            <Zap size={28} />
          </div>
          <div className="auth-logo__text">
            <span className="auth-logo__title">DevPad</span>
            <span className="auth-logo__subtitle">Developer Headquarters</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="auth-card">
          <div className="auth-card__header">
            <h1>Create your account</h1>
            <p>Start building and organizing your projects today</p>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {/* Error Message */}
            {(formError || error) && (
              <div className="auth-form__error">
                {formError || error}
              </div>
            )}

            {/* Name Field */}
            <div className="auth-form__group">
              <label htmlFor="name">Full Name</label>
              <div className="auth-form__input-wrapper">
                <User size={18} className="auth-form__input-icon" />
                <input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="name"
                />
              </div>
            </div>

            {/* Email Field */}
            <div className="auth-form__group">
              <label htmlFor="email">Email</label>
              <div className="auth-form__input-wrapper">
                <Mail size={18} className="auth-form__input-icon" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="auth-form__group">
              <label htmlFor="password">Password</label>
              <div className="auth-form__input-wrapper">
                <Lock size={18} className="auth-form__input-icon" />
                <input
                  id="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </div>
              
              {/* Password Requirements */}
              {password && (
                <div className="auth-form__password-checks">
                  <span className={passwordChecks.length ? 'valid' : ''}>
                    <Check size={14} /> At least 8 characters
                  </span>
                  <span className={passwordChecks.number ? 'valid' : ''}>
                    <Check size={14} /> Contains a number
                  </span>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="auth-form__group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="auth-form__input-wrapper">
                <Lock size={18} className="auth-form__input-icon" />
                <input
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                />
              </div>
              {confirmPassword && (
                <div className="auth-form__password-checks">
                  <span className={passwordChecks.match ? 'valid' : ''}>
                    <Check size={14} /> Passwords match
                  </span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="auth-form__submit btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight size={18} />
                </>
              )}
            </button>

            {/* Divider */}
            <div className="auth-form__divider">
              <span>or</span>
            </div>

            {/* Google Sign-Up Button */}
            <div 
              ref={googleButtonRef} 
              className="auth-form__google-btn"
            ></div>
          </form>

          {/* Footer Link */}
          <div className="auth-card__footer">
            <p>
              Already have an account?{' '}
              <Link to="/login">Sign in</Link>
            </p>
          </div>
        </div>

        {/* Bottom Text */}
        <p className="auth-footer">
          Build, store, and display your projects with precision context and AI assistance.
        </p>
      </div>
    </div>
  );
};

export default Register;

